/**
 * VacationResponderDialog — per-account out-of-office auto-reply config.
 *
 * Toggle + subject / body / optional window. Saving writes the config; the sync
 * sends the auto-replies (once per unique sender per window, loop-suppressed).
 * Password / app-password accounts only — OAuth accounts can't send yet, so a
 * note explains why the toggle is unavailable there. Mirrors SpamSettingsDialog.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useDialogAccount } from '../hooks/useDialogAccount';
import { DialogAccountPicker } from './DialogAccountPicker';
import { useVacationSettings, useSetVacationSettings } from '../hooks/useVacation';

export interface VacationResponderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seed account; the dialog has its own in-popup picker to switch. */
  defaultAccountId?: string;
}

/** ISO → the value a `datetime-local` input expects (local `YYYY-MM-DDTHH:mm`). */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` value → ISO (treating it as local time), or null when empty. */
function fromLocalInput(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function VacationResponderDialog({
  open,
  onOpenChange,
  defaultAccountId,
}: VacationResponderDialogProps) {
  const { accounts, selectedAccountId, setSelectedAccountId } = useDialogAccount(defaultAccountId);
  const { data, isLoading } = useVacationSettings(selectedAccountId, open);
  const save = useSetVacationSettings(selectedAccountId ?? '');

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');

  // Whether the selected account can send an auto-reply (OAuth send isn't wired).
  const selectedAccount = accounts.find((a) => a.accountId === selectedAccountId);
  const canSend = selectedAccount ? selectedAccount.authType !== 'oauth2' : true;

  // Seed the editors from the saved config once loaded.
  useEffect(() => {
    if (data) {
      setSubject(data.settings.subject);
      setBody(data.settings.body);
      setStartAt(toLocalInput(data.settings.startAt));
      setEndAt(toLocalInput(data.settings.endAt));
    }
  }, [data]);

  const enabled = data?.enabled ?? false;

  const saveConfig = () =>
    save.mutate({
      subject,
      body,
      startAt: fromLocalInput(startAt),
      endAt: fromLocalInput(endAt),
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vacation responder</DialogTitle>
          <DialogDescription>
            Auto-reply to incoming mail while you’re away. Each sender gets one reply per vacation
            period; mailing lists and automated mail are skipped.
          </DialogDescription>
        </DialogHeader>
        {!selectedAccountId ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Connect an account to set up a vacation responder.
          </p>
        ) : isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-4">
            <DialogAccountPicker
              accounts={accounts}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
            />

            {!canSend && (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-muted-foreground">
                This account is connected via OAuth, which can’t send mail yet. Reconnect with an
                app password to use the vacation responder.
              </p>
            )}

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={enabled}
                disabled={save.isPending || !canSend}
                onChange={(e) => save.mutate({ enabled: e.target.checked })}
              />
              <span>
                Enable vacation responder
                <span className="block text-xs text-muted-foreground">
                  When on and within the window below (or immediately, if no window is set), new
                  inbox mail gets an automatic reply.
                </span>
              </span>
            </label>

            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="vacation-subject">
                Subject
              </label>
              <Input
                id="vacation-subject"
                value={subject}
                maxLength={200}
                placeholder="Out of office"
                disabled={save.isPending || !canSend}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="vacation-body">
                Message
              </label>
              <Textarea
                id="vacation-body"
                rows={6}
                value={body}
                maxLength={5000}
                placeholder="I'm currently away and will reply when I'm back."
                disabled={save.isPending || !canSend}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="vacation-start">
                  Start (optional)
                </label>
                <Input
                  id="vacation-start"
                  type="datetime-local"
                  value={startAt}
                  disabled={save.isPending || !canSend}
                  onChange={(e) => setStartAt(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="vacation-end">
                  End (optional)
                </label>
                <Input
                  id="vacation-end"
                  type="datetime-local"
                  value={endAt}
                  disabled={save.isPending || !canSend}
                  onChange={(e) => setEndAt(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button disabled={save.isPending || !canSend} onClick={saveConfig}>
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
